import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import LoadingButton from "@mui/lab/LoadingButton";
import { useTranslation } from "react-i18next";
import { UAParser } from "ua-parser-js";
import ContentStyle from "./ContentStyle";
import { useAppDispatch } from "../../redux-store";
import {
  loadLoginCodeStatus,
  resendLoginCode,
  verifyLoginCode,
} from "../../ducks/auth/login-code";

type Message = { severity: "error" | "info"; text: string };

const goToApp = () => {
  const parser = new UAParser(window.navigator.userAgent);
  window.location.replace(
    parser.getResult().device.type === "mobile" ? "/live-view" : "/"
  );
};

const LoginCodePage = () => {
  const { t } = useTranslation("auth");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [reqInProgress, setReqInProgress] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);

  // Nothing to enter a code for (e.g. the login expired, or the page was
  // opened directly): start over at the password.
  useEffect(() => {
    dispatch(loadLoginCodeStatus())
      .unwrap()
      .then(({ pending }) => {
        if (!pending) navigate("/welcome/login", { replace: true });
      })
      .catch(() => {
        // The thunk has already reported it and shown the error toast.
      });
  }, [dispatch, navigate]);

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setReqInProgress(true);
    setMessage(null);
    dispatch(verifyLoginCode({ code }))
      .unwrap()
      .then((result) => {
        if (result.ok) {
          goToApp();
          return;
        }
        setCode("");
        switch (result.error) {
          case "INVALID_CODE":
            setMessage({
              severity: "error",
              text: t("loginCodePage.invalidCode", {
                count: result.attemptsLeft,
              }),
            });
            break;
          case "TOO_MANY_ATTEMPTS":
            setMessage({
              severity: "error",
              text: t("loginCodePage.tooManyAttempts"),
            });
            break;
          case "EXPIRED":
          case "NO_CODE":
            setMessage({
              severity: "error",
              text: t("loginCodePage.expired"),
            });
            break;
        }
      })
      .catch(() => {
        // The thunk has already reported it and shown the error toast.
      })
      .finally(() => setReqInProgress(false));
  };

  const handleResend = () => {
    setReqInProgress(true);
    setMessage(null);
    dispatch(resendLoginCode())
      .unwrap()
      .then((result) =>
        setMessage(
          result.ok
            ? { severity: "info", text: t("loginCodePage.codeSent") }
            : {
                severity: "error",
                text: t("loginCodePage.waitBeforeResend", {
                  count: result.waitSeconds,
                }),
              }
        )
      )
      .catch(() => {
        // The thunk has already reported it and shown the error toast.
      })
      .finally(() => setReqInProgress(false));
  };

  return (
    <Container>
      <ContentStyle>
        <h1>{t("loginCodePage.heading")}</h1>
        <p>{t("loginCodePage.instructions")}</p>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth
                autoFocus
                name="code"
                label={t("loginCodePage.codeLabel")}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                required
              />
            </Grid>
            <Grid size={12}>
              <LoadingButton
                loading={reqInProgress}
                fullWidth
                type="submit"
                variant="contained"
              >
                {t("loginCodePage.submitButtonText")}
              </LoadingButton>
            </Grid>
            <Grid size={12}>
              <Button fullWidth disabled={reqInProgress} onClick={handleResend}>
                {t("loginCodePage.resendButtonText")}
              </Button>
            </Grid>
            {message && (
              <Grid size={12}>
                <Alert severity={message.severity}>{message.text}</Alert>
              </Grid>
            )}
          </Grid>
        </form>
      </ContentStyle>
    </Container>
  );
};

export default LoginCodePage;
